/**
 * Prefix property and object properties
 * Prefix<{ foo: boolean }, 'bar_'> => { bar_foo: boolean }
 */
export type Prefix<T, TPrefix extends string> = T extends string
  ? `${TPrefix}${T}`
  : T extends object
    ? {
        [K in keyof T as Prefix<K, TPrefix>]: T[K];
      }
    : never;

/**
 * Exclude null and undefined from fields
 */
export type NonNullableObject<T extends object> = {
  [K in keyof T]: NonNullable<T[K]>;
};

/**
 * Convert type from one to another
 * ConvertType<{ foo: bar }, bar, car> => { foo: car }
 */
export type ConvertType<T, TFrom, TTo> = {
  [K in keyof T]: T[K] extends TFrom ? TTo : T[K];
};

export type PickRequiredProperties<T> = {
  [K in keyof T as string extends K
    ? never
    : number extends K
      ? never
      : Record<string, unknown> extends Pick<T, K>
        ? never
        : K]: T[K];
};

export type PickOptionalProperties<T> = {
  [K in keyof T as string extends K
    ? never
    : number extends K
      ? never
      : Record<string, unknown> extends Pick<T, K>
        ? K
        : never]: T[K];
};

export type GetMandatoryKeys<T> = {
  [P in keyof T]: T[P] extends Exclude<T[P], undefined> ? P : never;
}[keyof T];

export type UndefinedKeysAsOptional<T> = Partial<T> & Pick<T, GetMandatoryKeys<T>>;

export type Exact<A, W> = W extends unknown
  ? [A] extends [W]
    ? A extends string | number | boolean | bigint
      ? A
      : {
          [K in keyof A]: K extends keyof W ? Exact<A[K], W[K]> : never;
        }
    : W
  : never;

export type Equals<X, Y> =
  (<T>() => T extends X ? 1 : 2) extends <T>() => T extends Y ? 1 : 2 ? true : false;

export type AssertTrue<A extends true> = A;

export type InferConstObjectType<T extends object> = T[keyof T];

/**
 * Transform `A | B` into `A & B`
 */
export type UnionToIntersection<U> = (U extends never ? never : (arg: U) => never) extends (
  arg: infer I,
) => void
  ? I
  : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- actually needed in this case
export type UnionToTuple<T, A extends any[] = []> =
  UnionToIntersection<T extends never ? never : (t: T) => T> extends (_: never) => infer W
    ? UnionToTuple<Exclude<T, W>, [...A, W]>
    : A;

/**
 * Extract a type from a union by its discriminator
 */
export type ExtractByDiscriminator<
  T extends Record<K, string>,
  K extends keyof T,
  V extends T[K],
> = Extract<T, Record<K, V>>;

/**
 * Zod error issue type for duck-typing (avoids module resolution issues across workspaces)
 */
export interface ZodIssueLike {
  path: PropertyKey[];
  message: string;
  code?: string;
  expected?: unknown;
  received?: unknown;
}

/**
 * Zod error type for duck-typing (avoids module resolution issues across workspaces)
 */
export interface ZodErrorLike {
  name: 'ZodError';
  stack?: string;
  issues: ZodIssueLike[];
}

/**
 * Check if error is zod error (uses duck-typing for cross-workspace compatibility)
 */
export function isZodError(err: unknown): err is ZodErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    err.name === 'ZodError' &&
    'issues' in err &&
    Array.isArray(err.issues)
  );
}
